import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { createPortal } from 'react-dom';
import { TutorialDemonstracaoCoachBackdrop } from './TutorialDemonstracaoCoachBackdrop';
import {
	computeTutorialHeaderNavSpotlight,
	isTutorialHeaderInteractionTarget,
	TUTORIAL_DEMO_HEADER_LAYOUT_SELECTORS,
	type CoachSpotlightRect,
} from './tutorialDemonstracaoCoachSpotlight';

const WELCOME_BALLOON_MAX_WIDTH_PX = 300;

type Props = {
	onContinue: () => void;
};

const TutorialDemonstracaoCallWelcomeCoachBalloon = ({ onContinue }: Props) => {
	const backdropMaskId = useId().replace(/:/g, '');
	const portalRef = useRef<HTMLDivElement>(null);
	const [headerNavSpotlight, setHeaderNavSpotlight] =
		useState<CoachSpotlightRect | null>(null);

	useLayoutEffect(() => {
		const updateHeaderNavSpotlight = () => {
			setHeaderNavSpotlight(computeTutorialHeaderNavSpotlight());
		};

		updateHeaderNavSpotlight();

		const observer = new ResizeObserver(updateHeaderNavSpotlight);
		const mutationObserver = new MutationObserver(updateHeaderNavSpotlight);

		for (const selector of TUTORIAL_DEMO_HEADER_LAYOUT_SELECTORS) {
			document
				.querySelectorAll<HTMLElement>(selector)
				.forEach((element) => observer.observe(element));
		}

		mutationObserver.observe(document.body, {
			childList: true,
			subtree: true,
			attributes: true,
		});

		window.addEventListener('resize', updateHeaderNavSpotlight);
		window.addEventListener('scroll', updateHeaderNavSpotlight, true);

		return () => {
			observer.disconnect();
			mutationObserver.disconnect();
			window.removeEventListener('resize', updateHeaderNavSpotlight);
			window.removeEventListener(
				'scroll',
				updateHeaderNavSpotlight,
				true,
			);
		};
	}, []);

	useEffect(() => {
		const blockDisallowedInteraction = (event: Event) => {
			const target = event.target;
			if (!(target instanceof Node)) return;

			const continueButton = portalRef.current?.querySelector(
				'button[aria-label="Continuar"]',
			);
			if (continueButton?.contains(target)) return;
			if (isTutorialHeaderInteractionTarget(target)) return;

			event.preventDefault();
			event.stopPropagation();
		};

		document.addEventListener(
			'pointerdown',
			blockDisallowedInteraction,
			true,
		);
		document.addEventListener('click', blockDisallowedInteraction, true);

		return () => {
			document.removeEventListener(
				'pointerdown',
				blockDisallowedInteraction,
				true,
			);
			document.removeEventListener(
				'click',
				blockDisallowedInteraction,
				true,
			);
		};
	}, []);

	const backdropHoles = headerNavSpotlight ? [headerNavSpotlight] : [];

	return createPortal(
		<div
			ref={portalRef}
			className="pointer-events-none fixed inset-0 z-[200] flex items-center justify-center p-4"
		>
			{backdropHoles.length > 0 ? (
				<TutorialDemonstracaoCoachBackdrop
					maskId={backdropMaskId}
					holes={backdropHoles}
				/>
			) : (
				<div className="absolute inset-0 bg-black/55" aria-hidden />
			)}
			<div
				className="pointer-events-auto relative w-full rounded-xl border border-zinc-200/90 bg-white px-4 py-5 shadow-2xl"
				style={{ maxWidth: WELCOME_BALLOON_MAX_WIDTH_PX }}
				role="dialog"
				aria-labelledby="demo-call-coach-welcome"
			>
				<div className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-x-2 gap-y-2">
					<p
						id="demo-call-coach-welcome"
						className="text-sm leading-relaxed text-zinc-600"
					>
						Está é a sua sala de atendimento. Vamos explorar juntos
						tudo que você tem disponível para tornar sua sessão mais
						tranquila e organizada.
					</p>
					<button
						type="button"
						onClick={onContinue}
						className="flex size-7 shrink-0 items-center justify-center self-end rounded-full bg-zinc-900 text-white transition-colors hover:bg-zinc-800"
						aria-label="Continuar"
					>
						<ChevronRight className="size-3.5" strokeWidth={2.5} />
					</button>
				</div>
			</div>
		</div>,
		document.body,
	);
};

export { TutorialDemonstracaoCallWelcomeCoachBalloon };
